import { useCallback, useEffect, useMemo, useState } from 'react';
import { DiscoveryToolbar, SectionHeading, LoadError, LoadSkeleton } from '../components/ui';
import { Select } from '../components/forms';
import { useAdminSupport } from '../hooks';
import { isConfirmedEmpty, isFirstLoad } from '../stores/loadState';
import { c } from '../styles/theme';
import type { AdminSupportTicket } from '../types/domain';
import AdminSupportDetail from '../components/admin/AdminSupportDetail';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'resolved', label: 'Resolved' },
] as const;

const LEVEL_BADGE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.02em',
  background: c.accentSoft,
  border: `1px solid ${c.accentBorder}`,
  color: c.accent,
} as const;
const TICKET_LIST_MAX_HEIGHT = 'calc(8 * 92px + 7 * 8px)';

type FilterValue = (typeof FILTER_OPTIONS)[number]['value'];

function isResolvedTicket(ticket: Pick<AdminSupportTicket, 'status'>): boolean {
  return ticket.status === 'Resolved';
}

function TicketRow({
  ticket,
  selected,
  onSelect,
}: {
  ticket: AdminSupportTicket;
  selected: boolean;
  onSelect: () => void;
}) {
  const preview = ticket.message?.trim() || 'No message provided.';
  const resolved = isResolvedTicket(ticket);
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${selected ? c.accentBorder : c.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        background: selected ? c.accentSoft : c.surface1,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          margin: '0 0 3px',
        }}
      >
        <p
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 13,
            margin: 0,
            color: c.text1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ticket.subject}
        </p>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          {ticket.hasUnreadUserMessage ? (
            <span
              aria-label="Unread customer message"
              title="Unread customer message"
              style={{ width: 8, height: 8, borderRadius: 999, background: c.danger }}
            />
          ) : null}
          <span
            aria-label={resolved ? 'Resolved ticket' : 'Unresolved ticket'}
            title={resolved ? 'Resolved' : 'Unresolved'}
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              flexShrink: 0,
              background: resolved ? c.success : c.accent,
            }}
          />
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          margin: '0 0 4px',
        }}
      >
        <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{ticket.requesterEmail}</p>
        <span style={LEVEL_BADGE_STYLE}>{`level ${ticket.requesterPurchaseLevel}`}</span>
      </div>
      <p
        style={{
          fontSize: 11,
          color: c.text3,
          margin: 0,
          lineHeight: 1.45,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {ticket.requesterName} · {ticket.category} · {ticket.status} · {ticket.date}
        {preview ? ` · ${preview}` : ''}
      </p>
    </button>
  );
}

export default function AdminSupportInbox() {
  const {
    tickets,
    selectedTicketThread,
    load,
    threadLoad,
    loadTickets,
    loadTicketThread,
    refreshTickets,
    clearTicketThread,
    setTicketStatus,
    sendReply,
    isPending,
  } = useAdminSupport();
  const [filter, setFilter] = useState<FilterValue>('all');
  const [search, setSearch] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const query = search.trim().toLowerCase();

  useEffect(() => {
    if (!selectedTicketId) clearTicketThread();
  }, [selectedTicketId, clearTicketThread]);

  const visibleTickets = useMemo(() => {
    const filtered = tickets.filter((ticket) => {
      const matchesFilter =
        filter === 'all'
          ? true
          : filter === 'resolved'
            ? ticket.status === 'Resolved'
            : ticket.status !== 'Resolved';
      if (!matchesFilter) return false;
      if (!query) return true;
      const haystack = [
        ticket.subject,
        ticket.category,
        ticket.requesterName,
        ticket.requesterEmail,
        ticket.message ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
    return [
      ...filtered.filter((ticket) => ticket.hasUnreadUserMessage),
      ...filtered.filter((ticket) => !ticket.hasUnreadUserMessage && !isResolvedTicket(ticket)),
      ...filtered.filter((ticket) => !ticket.hasUnreadUserMessage && isResolvedTicket(ticket)),
    ];
  }, [tickets, filter, query]);

  const selectedTicketSummary = selectedTicketId
    ? (visibleTickets.find((ticket) => ticket.id === selectedTicketId) ??
      tickets.find((ticket) => ticket.id === selectedTicketId) ??
      null)
    : null;
  const selectedTicketThreadForId =
    selectedTicketThread?.id === selectedTicketId ? selectedTicketThread : null;

  const closeTicketDetail = useCallback(() => setSelectedTicketId(null), []);
  const retryTicketThread = useCallback(() => {
    if (!selectedTicketId) return;
    void loadTicketThread(selectedTicketId);
  }, [loadTicketThread, selectedTicketId]);
  const changeTicketStatus = useCallback(
    (status: Parameters<typeof setTicketStatus>[1]) => {
      if (!selectedTicketId) return;
      void setTicketStatus(selectedTicketId, status);
    },
    [selectedTicketId, setTicketStatus],
  );
  const sendTicketReply = useCallback(
    (message: string, closeTicket?: boolean) => {
      if (!selectedTicketId) return;
      void sendReply(selectedTicketId, message, closeTicket);
    },
    [selectedTicketId, sendReply],
  );

  return (
    <div>
      <SectionHeading
        as="h1"
        title="Support Inbox"
        subtitle="Admin-only view of all persisted support tickets"
        marginBottom={16}
      />

      {isFirstLoad(load, tickets.length > 0) ? (
        <LoadSkeleton rows={3} rowHeight={112} label="Loading support tickets…" />
      ) : load.status === 'error' && tickets.length === 0 ? (
        <LoadError
          message={load.error?.message ?? 'Could not load support tickets.'}
          onRetry={loadTickets}
        />
      ) : (
        <>
          <div>
            <DiscoveryToolbar
              query={search}
              onQueryChange={setSearch}
              refreshing={
                load.status === 'loading' ||
                (Boolean(selectedTicketId) && threadLoad.status === 'loading')
              }
              onRefresh={() => void refreshTickets(selectedTicketId)}
              searchLabel="Search support tickets"
              placeholder="Search support tickets by subject, requester, email or message"
              marginBottom={12}
              trailingAction={
                <Select
                  ariaLabel="Filter support tickets"
                  value={filter}
                  onChange={(value) => setFilter(value as FilterValue)}
                  options={FILTER_OPTIONS}
                  style={{ minWidth: 148, marginBottom: 0 }}
                />
              }
            />

            {isConfirmedEmpty(load, visibleTickets.length > 0) ? (
              <p style={{ fontSize: 12, color: c.text2, margin: '4px 0 0' }}>
                No support tickets have been submitted yet.
              </p>
            ) : visibleTickets.length === 0 ? (
              <p style={{ fontSize: 12, color: c.text2, margin: '4px 0 0' }}>
                No tickets match the current filter or search.
              </p>
            ) : (
              <div
                data-testid="admin-support-ticket-list"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  maxHeight: TICKET_LIST_MAX_HEIGHT,
                  overflowY: 'auto',
                  overscrollBehaviorY: 'contain',
                }}
              >
                {visibleTickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    selected={selectedTicketId === ticket.id}
                    onSelect={() => {
                      setSelectedTicketId(ticket.id);
                      void loadTicketThread(ticket.id);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <AdminSupportDetail
            ticket={selectedTicketThreadForId ?? selectedTicketSummary}
            thread={selectedTicketThreadForId}
            threadError={
              selectedTicketId && threadLoad.status === 'error'
                ? (threadLoad.error?.message ?? 'Could not load this ticket.')
                : null
            }
            pending={selectedTicketId ? isPending(selectedTicketId) : false}
            loading={Boolean(selectedTicketId) && threadLoad.status === 'loading'}
            onBack={closeTicketDetail}
            onRetry={retryTicketThread}
            onChangeStatus={changeTicketStatus}
            onSendReply={sendTicketReply}
          />
        </>
      )}
    </div>
  );
}
